import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";

const TIERS = [
  {
    name: "Core Ceremonies",
    label: "Weddings, Commitments, Vow Renewals",
    copy: "A full bespoke ceremony including consultation, questionnaire, script writing, revisions, and attendance on the day.",
    note: "Fee by arrangement. Please enquire for a quote.",
  },
  {
    name: "Family Ceremonies",
    label: "Namings, Blessings, Coming of Age",
    copy: "Ceremonies that welcome, celebrate, and mark the arrivals and thresholds of family life.",
    note: "Fee by arrangement. Please enquire for a quote.",
  },
  {
    name: "Remembrance",
    label: "Funerals, Celebrations of Life, Memorials",
    copy: "Karen approaches every remembrance ceremony with the full weight of care it deserves. Fees are discussed sensitively and in private.",
    note: "Please get in touch directly.",
  },
  {
    name: "Signature Ceremonies",
    label: "The Sovereign, The Highland, The Quiet, and others",
    copy: "Karen's most personalised offering. Every element chosen with complete intentionality. Priced individually.",
    note: "Fee by arrangement. Please enquire.",
  },
];

const INCLUDED = [
  "An initial consultation (phone or video)",
  "A detailed client questionnaire",
  "A ceremony script written entirely from scratch",
  "Up to three rounds of revisions",
  "A final script delivered in advance of the ceremony",
  "Attendance and officiating on the day",
  "Access to the SVV client portal",
  "A brief follow-up after the ceremony",
];

const Pricing = () => (
  <main data-testid="pricing-page">
    <section className="grain relative overflow-hidden bg-forestdeep pb-20 pt-40 text-cream lg:pb-28 lg:pt-52" data-testid="pricing-hero">
      <div className="mx-auto max-w-7xl px-6 sm:px-12">
        <p className="mb-6 text-[11px] uppercase tracking-[0.35em] text-gold">Fees</p>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl" data-testid="pricing-heading">
          <MaskedLine delay={0.2}>Ceremony Fees</MaskedLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="font-display mt-8 max-w-2xl text-xl italic text-cream/80 sm:text-2xl" data-testid="pricing-subheading">
            Every ceremony is different, and so is every fee. The figures below are a starting point for conversation.
          </p>
          <span className="gold-rule mt-10 block" />
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-3xl px-6 py-20 text-center lg:py-28" data-testid="pricing-philosophy">
      <Reveal>
        <span className="gold-rule mx-auto mb-10 block" />
        <p className="font-display text-2xl leading-relaxed text-ink sm:text-[1.7rem] sm:leading-[1.6]">
          Karen does not believe in a one-size fee. A small graveside service and a full Highland wedding ceremony are
          not the same piece of work, and the fee should reflect that. What you will always receive is the same level
          of care, preparation, and craft, whatever the ceremony.
        </p>
        <span className="gold-rule mx-auto mt-10 block" />
      </Reveal>
    </section>

    <section className="mx-auto max-w-7xl px-6 pb-20 sm:px-12 lg:pb-28" data-testid="pricing-tiers">
      <div className="grid gap-6 sm:grid-cols-2">
        {TIERS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.08}>
            <article
              data-testid={`pricing-card-${i + 1}`}
              className="group flex h-full flex-col border border-forest/15 bg-cream p-10 shadow-[0_10px_30px_-22px_rgba(28,51,42,0.45)] transition-all duration-500 hover:-translate-y-1 hover:border-gold hover:shadow-[0_20px_45px_-22px_rgba(201,168,76,0.5)]"
            >
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold">{t.label}</p>
              <h3 className="font-display mt-4 text-3xl text-forest">{t.name}</h3>
              <span className="mt-5 block h-px w-10 bg-gold/60 transition-[width] duration-500 group-hover:w-20" />
              <p className="mt-5 flex-1 text-sm leading-[1.9] text-slate2">{t.copy}</p>
              <p className="font-display mt-7 text-lg italic text-mulberry" data-testid={`pricing-note-${i + 1}`}>
                {t.note}
              </p>
            </article>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-3xl px-6 pb-20 text-center lg:pb-28" data-testid="pricing-deposit-section">
      <Reveal>
        <div className="border border-gold/50 bg-creamdeep/50 p-10 lg:p-12">
          <p className="mb-3 text-[11px] uppercase tracking-[0.35em] text-gold">Booking Deposit</p>
          <h2 className="font-display text-3xl text-forest sm:text-4xl" data-testid="pricing-deposit-heading">
            Ready to confirm your date?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-sm leading-[1.9] text-slate2">
            A £100 booking deposit secures your date in Karen's diary. Please note the deposit is non-refundable, as
            the date is then held for you alone.
          </p>
          <a
            href="https://buy.stripe.com/7sYeV66xvbnPbbD5PQ8Zq00"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pricing-deposit-button"
            className="group mt-8 inline-flex items-center gap-3 bg-gold px-10 py-4 text-[12px] uppercase tracking-[0.25em] text-forestdeep transition-colors duration-500 hover:bg-forest hover:text-cream"
          >
            Secure Your Date: £100 Deposit
            <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
          </a>
        </div>
      </Reveal>
    </section>

    <section className="grain relative bg-forestdeep py-20 text-cream lg:py-28" data-testid="pricing-included">
      <div className="mx-auto max-w-4xl px-6 sm:px-12">
        <Reveal className="mb-12 text-center">
          <p className="mb-4 text-[11px] uppercase tracking-[0.35em] text-gold">Always</p>
          <h2 className="font-display text-3xl sm:text-4xl" data-testid="included-heading">
            What every ceremony fee includes
          </h2>
        </Reveal>
        <Reveal>
          <ul className="mx-auto grid max-w-2xl gap-5 sm:grid-cols-2" data-testid="included-list">
            {INCLUDED.map((item, i) => (
              <li key={item} className="flex items-start gap-4" data-testid={`included-item-${i + 1}`}>
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                <span className="text-sm leading-relaxed text-cream/85">{item}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal className="mx-auto mt-14 max-w-2xl">
          <div className="border-l-2 border-gold pl-6" data-testid="travel-note">
            <p className="font-display text-xl italic text-gold">A note on travel</p>
            <p className="mt-3 text-sm leading-[1.9] text-cream/75">
              Karen is based in Alyth, Perthshire. Travel to venues across Perth and Kinross is included. A travel
              supplement applies for venues further afield; this will always be discussed and agreed in advance.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="mx-auto max-w-3xl px-6 py-24 text-center lg:py-32" data-testid="pricing-cta-section">
      <Reveal>
        <h2 className="font-display text-4xl text-forest sm:text-5xl" data-testid="pricing-cta-heading">
          Ready to Talk?
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-sm leading-[1.9] text-slate2 sm:text-base">
          Tell Karen about the occasion and she will come back to you with an honest, individual quote.
        </p>
        <Link
          to="/contact"
          data-testid="pricing-cta-button"
          className="group mt-10 inline-flex items-center gap-3 bg-forest px-10 py-4 text-[12px] uppercase tracking-[0.25em] text-cream transition-colors duration-500 hover:bg-mulberry"
        >
          Start your ceremony story
          <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
        </Link>
        <p className="mt-5 text-xs italic text-slate2/70">No obligation, just a conversation.</p>
      </Reveal>
    </section>
  </main>
);

export default Pricing;
